import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getNotificationEmailPreferences, getNotificationsData, getPreferencesWebsite, markNotificationsRead } from '../../../api/diagnose_dashboard/notifications';
import { accountFeaturesToAvailableDiagnoseMetrics } from '../../../helpers/diagnose/metrics';
import EmailPreferencesModal from './components/EmailPreferencesModal';
import NotificationTabItem from './components/NotificationTabItem';
import { checkNotificationTypesToPermissions, formatNotificationsData, defaulFilterObj } from './helpers';
import dayjs from 'dayjs';
import { Button, Tabs, Typography } from 'antd';

const { Title } = Typography;

const DiagnoseNotifications = () => {
  
  const TAB = {
    NEW: 'new',
    REVIEWED: 'reviewed',
  };
  const [showEmailPreference, setShowEmailPreference] = useState(false);
  const accountState = useSelector((state) => state.accountState);
  const currentUser = accountState?.getIn(['userDetails', 'value']);
  const accountId = currentUser?.accountId;
  const metrics = useMemo(() => {
    return accountFeaturesToAvailableDiagnoseMetrics(
      currentUser.accountFeatures,
      currentUser.allFeatureAccess || currentUser.featureAccess,
    );
  }, [currentUser.accountFeatures, currentUser.allFeatureAccess, currentUser.featureAccess]);
  const alertTypes = useMemo(
    () => checkNotificationTypesToPermissions(currentUser.accountFeatures),
    [currentUser.accountFeatures],
  );
  const [activeTab, setActiveTab] = useState(TAB.NEW);
  const [isLoading, setLoading] = useState(true);
  const [isLoadingMore, setLoadingMore] = useState(false);
  const [result, setResult] = useState({});
  const [propertyList, setPropertyList] = useState([]);
  const [savedUserPreferences, setSavedUserPreferences] = useState([
      {
        email: '',
        first_name: '',
        last_name: '',
        emailing_enabled: false,
        frequency: 24,
        preferences: [],
        websites: [],
        initialStatus: false,
        id: null
      },
  ]);
  const pageSize = 50;
  const today = dayjs()
  const thirtyDaysAgo = dayjs().subtract(30, 'days')

  const [appliedTabFilters, setAppliedTabFilters] = useState({
    [TAB.NEW]: defaulFilterObj,
    [TAB.REVIEWED]: defaulFilterObj
  })

  const getNotifications = useCallback(
    async ({ tab, filter, page = 1, size = pageSize }) => {
      let type = filter?.type?.join(";");
      if(type?.length === 0) {
        type = alertTypes.map(alt => alt.value)?.join(";");
      }
      let metric = filter?.metric?.join(";");
      if(metric?.length === 0) {
        metric = metrics?.join(";");
      }
      const dateFrom = (filter?.dateRange[0] ?? thirtyDaysAgo).format('YYYY-MM-DD');
      const dateTo = (filter?.dateRange[1] ?? today).format('YYYY-MM-DD');
      

      const getData = getNotificationsData({ metric, type, page, isSeen: tab === TAB.REVIEWED, pageSize: size, dateFrom, dateTo });
      return await getData;
    },[metrics, alertTypes]
  );

  const startGetNotifications = async (query, setLoadingState, /* options */ { withReset } = {}) => {
    if (withReset) {
      setResult({
        ...result,
        [query.tab]: undefined
      });
    }
    setLoadingState(true);
    const notifications = await getNotifications(query);

    setResult((results) => {
      const newData = formatNotificationsData(notifications, results?.[query.tab])
      return {
        ...results,
        [query.tab]:  newData
      };
    });
    setLoadingState(false);
  };

  const fetchPropertyList = useCallback(async () => {
    try {
      const resp = await getPreferencesWebsite({ accountId });
      let properties = [];
  
      if (!currentUser?.allDiagnoseSiteAccess && currentUser.diagnoseAccess?.size) {
        properties = resp.results.filter((property) =>
          currentUser.diagnoseAccess?.includes(property.id)
        );
      } else {
        properties = resp.results;
      }
      setPropertyList(properties);
      return properties;
    } catch (error) {
      console.error("Error fetching properties:", error);
      return [];
    }
  }, [accountId, currentUser]);
  
  const fetchUserPreferences = useCallback(
    async (availableProperties) => {
      if (!(currentUser.accountId === 22 || !currentUser.masqed)) {
        return;
      }
  
      try {
        const users = await getNotificationEmailPreferences(currentUser);
  
        if (users.length) {
          const preference = users.map((user) => {
            const filteredWebsites = user.websites.filter((w) =>
              availableProperties.some((property) => property.id === w.id)
            );
  
            return {
              ...user,
              websites: filteredWebsites,
              initialStatus: user.emailing_enabled,
            };
          });
          setSavedUserPreferences(preference);
        }
      } catch (error) {
        console.error("Error fetching user preferences:", error);
      }
    },
    [currentUser]
  );
  
  useEffect(() => {
      const fetchAllData = async () => {
        setLoading(true);
        const propertyList = await fetchPropertyList();
        if ( propertyList ) await fetchUserPreferences(propertyList );
        setLoading(false);
      };
      fetchAllData();
  },[]);

  const getAdditionalData = useCallback(async () => {
    if (pageSize * result[activeTab]?.page < result[activeTab]?.count) {
      await startGetNotifications(
        { tab: activeTab, filter: appliedTabFilters[activeTab], page: result[activeTab]?.page + 1 },
        setLoadingMore,
      );
    }
  }, [activeTab, appliedTabFilters, result, pageSize]);

  const onMarkNotificationsRead = async (body, /* options */ { refetchList = false } = {}) => {
    const { type, metric,  dateRange} =  appliedTabFilters[activeTab];
    const dateFrom = (dateRange[0] ?? thirtyDaysAgo).format('YYYY-MM-DD');
    const dateTo = (dateRange[1] ?? today).format('YYYY-MM-DD');
    await markNotificationsRead(body, { 
      type, 
      metric: Array.isArray(metric) ? metric.join(';') : '', 
      dateFrom, 
      dateTo, 
      accountId: currentUser.accountId, 
      isSeen: false
    });
    if (refetchList) {
      await startGetNotifications({ tab: activeTab, filter: appliedTabFilters[activeTab] }, setLoading, { withReset: refetchList });
    }
  };

  useEffect(() => {
    const initialFilters = appliedTabFilters[activeTab];
    setAppliedTabFilters((prev) => ({ ...prev, [activeTab]: initialFilters }))
    startGetNotifications({ tab: activeTab, filter: initialFilters }, setLoading, { withReset: false });
  }, [activeTab]);

  const onApplyFilters = (filters = appliedTabFilters[activeTab]) => {
    setAppliedTabFilters((prev) => ({ ...prev, [activeTab]: filters }))
    startGetNotifications({ tab: activeTab, filter: filters }, setLoading, { withReset: true })
  }

  const items = [
    {
      key: TAB.NEW,
      label: (
        <span className="tab-new">
          Unread <span className="count">({result?.[TAB.NEW]?.count || 0})</span>
        </span>
      ),
      disabled: isLoadingMore,
      children: (
        <NotificationTabItem
          result={result?.[TAB.NEW]}
          isLoadingMore={isLoadingMore}
          isLoading={isLoading}
          activeTab={activeTab}
          getAdditionalData={getAdditionalData}
          alertTypes={alertTypes}
          metrics={metrics}
          markNotificationsRead={onMarkNotificationsRead}
          onApplyFilters={onApplyFilters}
          appliedFilters={appliedTabFilters[TAB.NEW]}
        />
      ),
    },
    {
      key: TAB.REVIEWED,
      label: <span>Read</span>,
      disabled: isLoadingMore,
      children: (
        <NotificationTabItem
          result={result?.[TAB.REVIEWED]}
          isLoadingMore={isLoadingMore}
          isLoading={isLoading}
          activeTab={activeTab}
          alertTypes={alertTypes}
          metrics={metrics}
          getAdditionalData={getAdditionalData}
          markNotificationsRead={onMarkNotificationsRead}
          onApplyFilters={onApplyFilters}
          appliedFilters={appliedTabFilters[TAB.REVIEWED]}
        />
      ),
    },
  ];

  return (
    <div className={'diagnose_container diagnose_notifications'}>
      <div className="headers main_header">
        <div className="header-container">
          <Title level={2}>Diagnose Alerts</Title>
          <Button
            disabled={!(currentUser.accountId === 22 || !currentUser.masqed)}
            onClick={() => setShowEmailPreference(true)}
            type="primary"
            loading={isLoading}
          >
            Email Preferences
          </Button>
        </div>
      </div>
      <Tabs
        className="diagnose-overview"
        onChange={setActiveTab}
        activeKey={activeTab}
        animated
        items={items}
        destroyInactiveTabPane={true}
      />
      {showEmailPreference && (
        <EmailPreferencesModal
          showEmailPreference={showEmailPreference}
          currentUser={currentUser}
          alertTypes={alertTypes}
          propertyList={propertyList}
          savedUserPreferences={savedUserPreferences}
          setSavedUserPreferences={setSavedUserPreferences}
          onClose={() => setShowEmailPreference(false)}
        />
      )}
    </div>
  );
};

export default DiagnoseNotifications;